"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
const ATTRS: ReadonlyArray<readonly [string, string]> = [["autocomplete", "off"], ["data-1p-ignore", "true"], ["data-lpignore", "true"], ["data-form-type", "other"]];
function applyAttrs(el: Element) {
  for (const [name, value] of ATTRS) {
    // Compare values, not just presence — an input with `autocomplete="email"`
    // would otherwise be left with autofill enabled.
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }
}
function applyToFields(node: Element) {
  if (node.tagName === "INPUT" || node.tagName === "TEXTAREA") applyAttrs(node);
  node.querySelectorAll("input, textarea").forEach(applyAttrs);
}
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Marks every <input> / <textarea> inside the ref as opt-out for 1Password,
 * LastPass, Dashlane, and native autofill. Re-checks the ref each render so
 * it correctly attaches when modal content mounts after `open=true`.
 */
export function useNoAutofillScope(ref) {
  const $ = _c(4);
  const attached = useRef(null);
  const observer = useRef(null);
  let t0;
  if ($[0] !== ref.current) {
    t0 = () => {
      const node = ref.current;
      if (node === attached.current) {
        return;
      }
      observer.current?.disconnect();
      observer.current = null;
      attached.current = node;
      if (!node) {
        return;
      }
      applyToFields(node);
      const o = new MutationObserver(_temp2);
      o.observe(node, {
        childList: true,
        subtree: true
      });
      observer.current = o;
    };
    $[0] = ref.current;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useIsoLayoutEffect(t0);
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      observer.current?.disconnect();
      observer.current = null;
      attached.current = null;
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
}
function _temp2(records) {
  for (const record of records) {
    record.addedNodes.forEach(_temp);
  }
}
function _temp(added) {
  if (added instanceof Element) {
    applyToFields(added);
  }
}